<template>
  <div v-loading="loading" element-loading-spinner="atom-audit-loader" style="min-height: 200px;">
    <template v-if="groups.length === 0 && !loading">
      <center>No Activity Audit Data Available</center>
    </template>

    <div v-for="(group, gIdx) in groups" :key="`g-${gIdx}-${group.key}`">
      <h2 style="margin-top: 30px; margin-bottom: 10px;">{{ group.key }}</h2>

      <div v-for="type in group.types" :key="`${gIdx}-${type.activityAuditTypeId}`" style="margin-top: 30px; margin-bottom: 20px;" class="activity-type-wrapper">
        <el-row :gutter="20">
          <el-col :span="16">
            <el-col :span="24">
              <div class="el-table el-table--mini">
                <div style="position: relative; float: right; margin: 0px 0px -10px 0px;">
                  <el-dropdown trigger="click" @command="handleTableCommands">
                    <span class="el-dropdown-link">
                      <i title="table actions" class="mdi mdi-dots-horizontal" style="font-size: 20px; cursor: pointer;"></i>
                    </span>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item :command="buildTableCommands(group, type)">Download</el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </div>

                <table class="totalsTable hiaTable dashboard-table">
                  <thead>
                    <tr>
                      <th>Activity Type</th>
                      <th class="textAlignRight" style="padding-right: 13px;">Total Number of Records</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="textAlignLeft">{{ type.name }}</td>
                      <td class="textAlignRight">{{ type.totalRecordsWithActivity }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </el-col>

            <el-col :span="24">
              <div style="margin-left: 20px; margin-top: 20px;">
                <el-table :data="type.activities" stripe empty-text="No Activities" class="tableNoWrap subTable" show-summary :summary-method="getSummaries">
                  <el-table-column prop="name" label="Activity" sortable min-width="100"></el-table-column>
                  <el-table-column prop="totalLogged" label="Frequency" sortable>
                    <template slot-scope="scope">
                      <div style="display: flex; justify-content: flex-end;">{{ scope.row.totalLogged }}</div>
                    </template>
                  </el-table-column>
                  <el-table-column prop="handledYes" label="Handled Correctly" sortable>
                    <template slot-scope="scope">
                      <div style="display: flex; justify-content: flex-end;">{{ scope.row.handledYes }}</div>
                    </template>
                  </el-table-column>
                  <el-table-column prop="handledNo" label="Handled Incorrectly" sortable>
                    <template slot-scope="scope">
                      <div style="display: flex; justify-content: flex-end;">{{ scope.row.handledNo }}</div>
                    </template>
                  </el-table-column>
                  <el-table-column prop="notAnswered" label="Not Answered" sortable>
                    <template slot-scope="scope">
                      <div style="display: flex; justify-content: flex-end;">{{ scope.row.notAnswered }}</div>
                    </template>
                  </el-table-column>
                  <el-table-column prop="accuracy" label="Accuracy" sortable>
                    <template slot-scope="scope">
                      <div style="display: flex; justify-content: flex-end;">{{ formatPercentage(scope.row.accuracy) }}</div>
                    </template>
                  </el-table-column>
                  <el-table-column label="Details" width="120" prop="details">
                    <template slot-scope="scope">
                      <el-button :disabled="!scope.row.accountNumbers" @click.native.prevent="openAccountDetails(scope.row)" plain round size="mini" icon="mdi mdi-details" :title="scope.row.accountNumbers ? 'Records Included' : 'No accounts for this activity'" circle />
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </el-col>
          </el-col>

          <el-col :span="8" style="text-align: center; margin-top: 28px;">
            <ActivityAuditBarChart :chartId="`IP_activityAudit_barchart_${gIdx}_${type.activityAuditTypeId}`" :typeName="type.name" :activities="type.activities" />
          </el-col>
        </el-row>
      </div>
    </div>

    <el-dialog :visible.sync="dialogVisible" center destroy-on-close width="75%">
      <div style="min-height: 250px;">
        <div style="float: left; text-align: left; margin: 0px 0px 10px 0px; font-weight: 400;">
          Account Numbers for <i> {{ detailsHeader }}</i>
        </div>

        <v-client-table class="hiaTable" ref="managedListsGrid" id="managedListsGrid" :data="accountDetails" :columns="['patientNumber', 'mrNumber', 'payName', 'dateOfService', 'reasonForChange', 'providerName', 'handled', 'worksheet']" :options="tableOptions" v-loading="loadingAccounts" element-loading-spinner="atom-audit-loader">
          <template slot="h__answer">
            <div>{{ detailsHeader }}</div>
          </template>
          <template slot="patientNumber" slot-scope="props">
            <template v-if="activeAccountLink">
              <router-link v-if="ipList" v-bind:to="`/reviews/inpatient/${props.row.drgtableId}`" target="_blank">{{ props.row.patientNumber }} </router-link>
            </template>
            <template v-else>
              <span>{{ props.row.patientNumber }}</span>
            </template>
          </template>

          <template slot="dateOfService" slot-scope="props">
            {{ props.row.dateOfService ? formatDate(props.row.dateOfService) : '' }}
          </template>

          <template slot="worksheet" slot-scope="props">
            <el-button v-if="ipList" @click="handleViewWorksheetsIP(props.row.drgtableId)" plain round><i class="mdi mdi-open-in-new tableIcon"></i>Worksheet </el-button>
          </template>
        </v-client-table>
      </div>
    </el-dialog>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import axios from '@/utilities/axios-global'
  import dashboardHelper from '@/mixins/dashboardHelper'
  import dateHelper from '@/mixins/date-helper'
  import ActivityAuditBarChart from '@/views/reports/highcharts/activityAuditBarChart.vue'

  export default {
    name: 'IP_ActivityAuditResults',
    components: { ActivityAuditBarChart },
    props: ['drgTableIds', 'codeTypes'],
    mixins: [dashboardHelper, dateHelper],
    data() {
      return {
        loading: false,
        groups: [],
        dialogVisible: false,
        detailsHeader: '',
        detailsDrgTableIds: null,
        loadingAccounts: false,
        accountDetails: [],
        tableOptions: {
          orderBy: {
            column: 'patientNumber',
            ascending: true
          },
          filterByColumn: true,
          headings: {
            patientNumber: 'Patient Number',
            mrNumber: 'MR Number',
            payName: 'Payor',
            dateOfService: 'Date of Service',
            reasonForChange: 'Reason for Change',
            providerName: 'Provider',
            handled: 'Handled',
            worksheet: 'Worksheet'
          },
          perPage: 20,
          texts: {
            filter: '',
            filterBy: '...'
          },
          highlightMatches: true,
          sortIcon: { is: 'none' }
        }
      }
    },
    methods: {
      async getData() {
        if (!this.tenantSetup || !this.tenantSetup.activityAuditsEnabled) {
          this.groups = []
          return
        }
        try {
          this.loading = true
          const response = await axios.post(`${this.baseApiUrl}/reports/activity-audit/summary`, {
            recordType: 'IP',
            recordIds: this.drgTableIds || ''
          })
          this.groups = (response.data && response.data.groups) || []
        } catch (err) {
          this.groups = []
        } finally {
          this.loading = false
        }
      },
      formatPercentage(value) {
        if (value === null || value === undefined) return '-'
        return (value * 100).toFixed(2) + '%'
      },
      async openAccountDetails(row) {
        if (!row.accountNumbers) {
          return
        }
        try {
          this.detailsHeader = row.name
          this.detailsDrgTableIds = row.accountNumbers
          this.loadingAccounts = true
          this.dialogVisible = true
          this.accountDetails = []
          await this.$nextTick()

          const response = await axios.post(`${this.baseApiUrl}/reports/activity-audit/account-details`, {
            recordType: 'IP',
            activityAuditActivityId: row.activityAuditActivityId,
            accountNumbers: row.accountNumbers
          })
          this.accountDetails = response.data || []
        } catch (e) {
          this.accountDetails = []
          this.$message({
            message: 'Error Getting Details',
            type: 'error'
          })
        } finally {
          this.loadingAccounts = false
        }
      },
      getSummaries(param) {
        const { columns, data } = param
        const sums = []
        columns.forEach((column, index) => {
          if (column.property === 'details') {
            sums[index] = ''
            return
          }
          if (index === 0) {
            sums[index] = 'Total'
            return
          }
          if (column.property === 'accuracy') {
            const totalYes = data.reduce((s, r) => s + Number(r.handledYes || 0), 0)
            const totalNo = data.reduce((s, r) => s + Number(r.handledNo || 0), 0)
            const denom = totalYes + totalNo
            sums[index] = denom > 0 ? ((totalYes / denom) * 100).toFixed(2) + '%' : '-'
          } else if (['totalLogged', 'handledYes', 'handledNo', 'notAnswered'].includes(column.property)) {
            sums[index] = data.reduce((s, r) => s + Number(r[column.property] || 0), 0)
          } else {
            sums[index] = ''
          }
        })
        return sums
      },
      handleTableCommands(command) {
        const { group, type } = command
        let csv = `Code type: ${group.key}\nActivity audit type: ${type.name}\n\n`
        csv += 'Activity,Frequency,Handled Correctly,Handled Incorrectly,Not Answered,Accuracy\n'
        ;(type.activities || []).forEach(a => {
          csv += `"${a.name}",${a.totalLogged},${a.handledYes},${a.handledNo},${a.notAnswered},${this.formatPercentage(a.accuracy)}\n`
        })
        const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), csv], { type: 'text/csv;charset=utf-8' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = 'activity_audit_results.csv'
        link.click()
      },
      buildTableCommands(group, type) {
        return { group, type }
      },
      handleViewWorksheetsIP(drgTableId) {
        const cacheBuster = new Date().getTime()
        const worksheetPath = this.inpatientReviewer() && this.ipList ? `/reviews/reports/inpatient/worksheets?fullscreen=true&dt=${cacheBuster}&ids=${drgTableId}` : `/my-projects/inpatient/worksheet/${drgTableId}?dt=${cacheBuster}&fullscreen=true`
        this.worksheetWindow = window.open(worksheetPath, 'codebook', 'height=850,width=1600')
      }
    },
    computed: {
      ...mapState('user/', ['baseApiUrl']),
      ...mapState('managedLists/tenantSetup/', ['tenantSetup']),
      ipList() {
        return true
      },
      proList() {
        return false
      },
      opList() {
        return false
      },
      activeAccountLink() {
        if ((this.inpatientReviewer() && this.ipList) || (this.providerReviewer() && this.proList) || (this.outpatientReviewer() && this.opList) || (this.ambulatoryReviewer() && this.opList)) {
          return true
        }
        return false
      }
    },
    mounted: async function() {
      await this.getData()
    },
    activated: async function() {
      await this.getData()
    },
    watch: {
      drgTableIds: async function() {
        await this.getData()
      }
    }
  }
</script>

<style scoped>
  .activity-type-wrapper + .activity-type-wrapper {
    margin-top: 60px !important;
  }

  .subTable >>> th:not(:first-child),
  .subTable >>> td:not(:first-child) {
    text-align: right;
    margin-right: 5px;
  }

  .tableNoWrap {
    width: 100%;
  }

  .totalsTable {
    border-spacing: 0px;
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
  }

  .textAlignRight {
    text-align: right !important;
    padding-right: 10px !important;
  }
</style>
